import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import PageHeader from "../components/PageHeader";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "../components/ui/dialog";
import { Switch } from "../components/ui/switch";
import { Plus, Trash, PencilSimple, User, Phone, EnvelopeSimple, MagnifyingGlass, Key, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { StatusBadge } from "../components/Semaforo";
import { useConfirm } from "../components/ConfirmDialog";

const empty = () => ({
  full_name: "", email: "", phone: "", cargo: "", area: "", photo_url: "", notes: "", is_active: true,
});

const LOGIN_ROLE_OPTIONS = [
  { v: "encargado_social", label: "Encargado Social" },
  { v: "encargado_ambiental", label: "Encargado Ambiental" },
  { v: "encargado_proyecto", label: "Encargado de Proyecto" },
  { v: "gerente", label: "Gerente" },
  { v: "consulta", label: "Consulta" },
  { v: "auditor", label: "Auditor" },
  { v: "admin_empresa", label: "Admin de la organización" },
];

const emptyLogin = () => ({ email: "", password: "", role: "encargado_social" });

export default function Staff() {
  const { user: me } = useAuth();
  const nav = useNavigate();
  const canManageUsers = ["super_admin", "admin_empresa", "gerente"].includes(me?.role);
  const confirm = useConfirm();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty());
  const [editingId, setEditingId] = useState(null);
  const [q, setQ] = useState("");
  const [loginDlg, setLoginDlg] = useState(null); // staff row
  const [loginForm, setLoginForm] = useState(emptyLogin());
  const [savingLogin, setSavingLogin] = useState(false);

  const load = async () => {
    const { data } = await api.get("/staff", { params: q ? { q } : {} });
    setItems(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    try {
      if (editingId) {
        await api.put(`/staff/${editingId}`, form);
        toast.success("Persona actualizada");
      } else {
        await api.post("/staff", form);
        toast.success("Persona agregada");
      }
      setOpen(false); setForm(empty()); setEditingId(null); load();
    } catch (e) { toast.error("Error al guardar"); }
  };

  const edit = (p) => {
    setForm({
      full_name: p.full_name || "", email: p.email || "", phone: p.phone || "",
      cargo: p.cargo || "", area: p.area || "", photo_url: p.photo_url || "",
      notes: p.notes || "", is_active: p.is_active,
    });
    setEditingId(p.id); setOpen(true);
  };

  const remove = async (id) => {
    if (!(await confirm({ title: "Eliminar persona", description: "¿Deseas eliminar a esta persona del equipo?", danger: true }))) return;
    await api.delete(`/staff/${id}`); load();
  };

  const openCreateLogin = (p) => {
    setLoginDlg(p);
    setLoginForm({ email: p.email || "", password: "", role: "encargado_social" });
  };

  const submitCreateLogin = async (e) => {
    e.preventDefault();
    if (!loginDlg) return;
    setSavingLogin(true);
    try {
      await api.post("/users", {
        email: loginForm.email,
        password: loginForm.password,
        full_name: loginDlg.full_name,
        role: loginForm.role,
        staff_id: loginDlg.id,
      });
      toast.success(`Login creado para ${loginDlg.full_name}`);
      setLoginDlg(null);
      setLoginForm(emptyLogin());
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "No se pudo crear el login");
    } finally {
      setSavingLogin(false);
    }
  };

  return (
    <div data-testid="staff-page">
      <PageHeader
        title="Equipo"
        description="Administra a las personas de la empresa que utilizan la plataforma y pueden ser asignadas como responsables en compromisos, proyectos y reuniones."
        actions={
          <Dialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) { setEditingId(null); setForm(empty()); } }}>
            <DialogTrigger asChild>
              <Button className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="new-staff-btn"><Plus weight="bold" /> Nueva persona</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{editingId ? "Editar persona" : "Nueva persona"}</DialogTitle></DialogHeader>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2"><Label>Nombre completo</Label><Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="staff-name-input" /></div>
                <div><Label>Cargo</Label><Input value={form.cargo} onChange={(e) => setForm({ ...form, cargo: e.target.value })} placeholder="Encargado Social, Ingeniero Ambiental…" /></div>
                <div><Label>Área</Label><Input value={form.area} onChange={(e) => setForm({ ...form, area: e.target.value })} placeholder="Sustentabilidad, Operaciones…" /></div>
                <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
                <div><Label>Teléfono</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} /></div>
                <div className="col-span-2"><Label>URL fotografía</Label><Input value={form.photo_url} onChange={(e) => setForm({ ...form, photo_url: e.target.value })} placeholder="https://…" /></div>
                <div className="col-span-2"><Label>Notas</Label><Textarea rows={3} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></div>
                <div className="col-span-2 flex items-center gap-3">
                  <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} id="active" />
                  <Label htmlFor="active">Activo (disponible para asignación)</Label>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                <Button onClick={submit} className="bg-slate-900 text-white" data-testid="save-staff-btn">Guardar</Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        }
      />

      <div className="px-6 py-6 space-y-4">
        {/* Filter bar */}
        <div className="flex items-end gap-3 rounded-md border border-slate-200 bg-white p-4">
          <div className="flex-1 max-w-md relative">
            <Label className="text-xs uppercase tracking-wider text-slate-500">Buscar</Label>
            <div className="relative">
              <MagnifyingGlass size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input className="pl-8" placeholder="Nombre, cargo o área…" value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} />
            </div>
          </div>
          <Button variant="secondary" onClick={load}>Aplicar</Button>
        </div>

        {/* Cards grid */}
        {items.length === 0 ? (
          <div className="rounded-md border border-dashed border-slate-300 bg-white px-6 py-16 text-center">
            <User size={32} className="mx-auto text-slate-300 mb-2" />
            <div className="text-sm text-slate-500">Aún no hay personas registradas. Crea la primera para asignar responsables.</div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map(p => (
              <div key={p.id} className="rounded-md border border-slate-200 bg-white p-4 flex gap-3" data-testid={`staff-card-${p.id}`}>
                <div className="h-14 w-14 rounded-full bg-slate-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                  {p.photo_url ? <img src={p.photo_url} alt="" className="h-full w-full object-cover" /> : <User size={24} className="text-slate-500" />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-medium text-slate-900 truncate">{p.full_name}</div>
                      <div className="text-xs text-slate-500 truncate">{[p.cargo, p.area].filter(Boolean).join(" · ") || "—"}</div>
                    </div>
                    {!p.is_active && <StatusBadge>Inactivo</StatusBadge>}
                  </div>
                  <div className="mt-2 space-y-0.5 text-xs text-slate-600">
                    {p.email && <div className="flex items-center gap-1.5 truncate"><EnvelopeSimple size={11} /> {p.email}</div>}
                    {p.phone && <div className="flex items-center gap-1.5"><Phone size={11} /> {p.phone}</div>}
                    {p.user_id ? (
                      <div className="flex items-center gap-1.5 text-brand-green-700">
                        <ShieldCheck size={11} weight="fill" />
                        Con login {p.user_role ? `· ${p.user_role}` : ""}
                        {p.user_is_active === false && <span className="text-red-600"> (inactivo)</span>}
                      </div>
                    ) : (
                      <div className="text-slate-400 italic">Sin login</div>
                    )}
                  </div>
                  <div className="mt-3 flex justify-end gap-1">
                    {canManageUsers && !p.user_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openCreateLogin(p)}
                        data-testid={`create-login-${p.id}`}
                        title="Crear login para esta persona"
                      >
                        <Key size={14} className="text-brand-orange-600" />
                      </Button>
                    )}
                    {canManageUsers && p.user_id && (
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => nav("/usuarios")}
                        data-testid={`goto-user-${p.id}`}
                        title="Gestionar acceso"
                      >
                        <ShieldCheck size={14} className="text-brand-green-600" />
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => edit(p)} data-testid={`edit-staff-${p.id}`}><PencilSimple size={14} /></Button>
                    <Button size="sm" variant="ghost" onClick={() => remove(p.id)}><Trash size={14} className="text-red-600" /></Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create login for a staff person */}
      <Dialog open={!!loginDlg} onOpenChange={(o) => { if (!o) { setLoginDlg(null); setLoginForm(emptyLogin()); } }}>
        <DialogContent className="max-w-md" data-testid="create-login-dialog">
          <DialogHeader>
            <DialogTitle>Crear login para {loginDlg?.full_name}</DialogTitle>
            <DialogDescription>
              Esta persona podrá ingresar a SIGSAOP con email y contraseña, y ver solo lo que su rol permite.
              La vinculación con su ficha del equipo se mantiene, por lo que seguirá siendo asignable en compromisos y proyectos.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={submitCreateLogin} className="space-y-4">
            <div>
              <Label>Email de acceso</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                value={loginForm.email}
                onChange={(e) => setLoginForm({ ...loginForm, email: e.target.value })}
                required
                autoFocus
              />
            </div>
            <div>
              <Label>Contraseña inicial</Label>
              <Input
                data-testid="login-password-input"
                type="text"
                value={loginForm.password}
                onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                placeholder="Mín. 6 caracteres"
                minLength={6}
                required
              />
            </div>
            <div>
              <Label>Rol</Label>
              <select
                data-testid="login-role-input"
                value={loginForm.role}
                onChange={(e) => setLoginForm({ ...loginForm, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-slate-200 bg-white px-3 text-sm"
                required
              >
                {LOGIN_ROLE_OPTIONS.map(r => <option key={r.v} value={r.v}>{r.label}</option>)}
              </select>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setLoginDlg(null)}>Cancelar</Button>
              <Button data-testid="save-login-btn" type="submit" disabled={savingLogin}>
                {savingLogin ? "Creando…" : "Crear login"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
